import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface ApplicationConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventTitle: string;
}

const ApplicationConfirmationModal: React.FC<ApplicationConfirmationModalProps> = ({
  isOpen,
  onClose,
  eventTitle,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleExploreMore = () => {
    navigate('/vybes');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="bg-theme-cards rounded-2xl shadow-card w-full max-w-md border border-theme-cards overflow-hidden"
      >
        {/* Success Icon */}
        <div className="bg-gradient-to-br from-red-500 to-pink-600 p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Check size={40} className="text-theme-primary" />
          </motion.div>
          <h2 className="text-2xl font-sora font-bold text-white mb-2">
            Request Sent! 🎉
          </h2>
          <p className="text-white/90 text-sm">
            Your application to <strong>{eventTitle}</strong> has been submitted
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* What Happens Next */}
          <div className="bg-theme-background/50 border border-white/10 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center">
              <span className="mr-2">⏳</span>
              What Happens Next
            </h3>
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>You'll get a notification when the host responds</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>Once accepted, check the Vybe chat for meeting details and to connect with everyone</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-1">•</span>
                <span>Check your Applications page for status updates</span>
              </li>
            </ul>
          </div>

          {/* While You Wait Banner */}
          <div className="bg-gradient-to-r from-theme-primary/10 to-pink-600/10 border border-theme-primary/20 rounded-xl p-4 text-center">
            <Sparkles size={24} className="mx-auto mb-2 text-theme-primary" />
            <p className="text-white font-semibold mb-1">While you wait...</p>
            <p className="text-white/70 text-sm mb-3">
              Explore more Vybes and grow your Tribe!
            </p>
            <button
              onClick={handleExploreMore}
              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-2 rounded-full font-semibold transition-all text-sm shadow-lg"
            >
              Explore More Vybes
            </button>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-3 border border-white/20 rounded-full text-white hover:bg-white/10 transition-colors font-semibold"
          >
            Got it!
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ApplicationConfirmationModal;
